<template>
  <BaseModalLarge
    :heading="
      (order?.amountOutstanding ?? 0) > 0
        ? t(compliantKey('register.saved_as_outstanding'))
        : t('register.saved_as_paid')
    "
    testId="register-overview"
    :label="order?.state"
    :loading="loading"
    noPadding
    :actions="actions"
    @action="onActionSelect"
    @close="close"
  >
    <template #aside>
      <Flex
        direction="column"
        :class="$style.aside"
      >
        <CustomerDetails
          :orderId="String(orderId)"
          :customer="customer"
          @updated="onSelectCustomer"
        />

        <BaseAlert
          v-if="isLoyaltyPointsEnabled && customer?.loyaltyPoints"
          size="s"
          :text="
            $t(
              'register.loyalty_points',
              {
                customer: customer.firstName,
                amount: customer.loyaltyPoints
              },
              customer.loyaltyPoints
            )
          "
          v-test="'register-overview-loyalty'"
        />
      </Flex>
    </template>

    <Flex
      v-if="order"
      direction="column"
      padding
    >
      <BaseAlert
        v-if="autoInvoiceGenerated"
        :text="$t('register.invoice.auto_generated', { number: invoiceDocument?.number })"
        color="success"
        :primaryAction="
          fiscalizationReady
            ? $t('global.actions.send_item', { item: $t('global.items.invoice', 1) })
            : undefined
        "
        @primaryAction="emailModalDocument = 'invoice'"
        v-test="'register-overview-auto-invoice-success'"
      />
      <BaseAlert
        v-else-if="autoInvoiceGenerationFailed"
        :text="$t('register.invoice.auto_generation_failed')"
        color="warning"
        :primaryAction="$t('register.invoice.issue_manually')"
        @primaryAction="issueInvoiceManually"
        v-test="'register-overview-auto-invoice-failed'"
      />
      <BaseAlert
        v-if="delayPrinting"
        :text="$t('register.print_warning')"
        icon="printer"
        color="warning"
        v-test="'register-overview-delayed-printing'"
      />
      <Details :order="order" />
      <Summary :order="order" />
      <Note
        v-if="order.comments"
        :note="order.comments"
      />
    </Flex>

    <template #footerSub>
      <BaseText
        :routerLink="{
          name: 'sale',
          params: {
            id: orderId
          }
        }"
        v-test="'view-sale'"
      >
        {{ $t('global.actions.view_sale') }}
      </BaseText>
    </template>
    <template #footer>
      <BaseButton
        v-if="hasFeatureFlag('module-appointments')"
        color="inverted"
        @click="bookNextAppointment"
        v-test="'register-overview-next-appointment'"
      >
        {{ $t('register.book_next_appointment') }}
      </BaseButton>
      <BaseButton
        :routerLink="{
          name: 'calendar'
        }"
        v-test="'register-overview-calendar'"
      >
        {{ $t('global.actions.go_to_calendar') }}
      </BaseButton>
    </template>

    <PrintUrl
      v-test="'print-url'"
      v-if="promptPrint"
      :key="printKey"
      :url="printUrl"
      :background="printBackground"
      @close="promptPrint = false"
    />

    <CreateNote
      v-if="showCreateNoteModal"
      :currentNote="order?.comments || undefined"
      @submit="updateNote"
      @close="showCreateNoteModal = false"
    />

    <EmailReceipt
      v-if="emailModalDocument"
      :type="emailModalDocument"
      :email="email"
      :documentId="emailModalDocument === 'invoice' ? invoiceDocument?.id : lastReceipt?.id"
      :orderId="orderId"
      @submit="emailModalDocument = null"
      @close="emailModalDocument = null"
    />

    <IssueInvoice
      v-if="showIssueInvoiceModal"
      :orderId="String(orderId)"
      @submit="handleIssueInvoiceSubmit"
      @close="showIssueInvoiceModal = false"
    />

    <SendPaymentLink
      v-if="showSendPaymentLinkModal"
      :orderId="String(orderId)"
      :email="email"
      @close="showSendPaymentLinkModal = false"
    />
  </BaseModalLarge>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PrintUrl from '@/components/PrintUrl.vue';
import config from '@/config';
import filters from '@/filters';
import { compliantKey } from '@/helpers/compliant-register';
import { flash } from '@/helpers/ui';
import CreateNote from '@/modals/CreateNote.vue';
import EmailReceipt from '@/modals/EmailReceipt.vue';
import SendPaymentLink from '@/modals/SendPaymentLink.vue';
import Details from '@/modals/sale/Details.vue';
import Note from '@/modals/sale/Note.vue';
import Summary from '@/modals/sale/summary/index.vue';
import IssueInvoice from '@/modules/register/modals/issue-invoice/index.vue';
import { useFiscalizationStore } from '@/modules/register/stores/fiscalization';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { segment } from '@/segment';
import { Action, usePreCreateStore } from '@/stores/calendar-pre-create';
import { useCompanyStore } from '@/stores/company';
import { useNativeAppStore } from '@/stores/native-app';
import { useUserStore } from '@/stores/user';
import { type Customer, type Order, RegisterDocumentEnum } from '@/types';
import CustomerDetails from './Customer.vue';
import { GET_ORDER, UPDATE_ORDER } from './graphql';

const props = withDefaults(
  defineProps<{
    orderId: number;
    invoiceJustIssued?: boolean;
  }>(),
  {
    invoiceJustIssued: false,
  },
);

const { onResult, loading, refetch } = useQuery(GET_ORDER, {
  id: props.orderId,
});

const order = ref<Order | null>(null);
const customer = ref<Customer | null>(null);
const email = ref(null);
const loadingPdf = ref(false);
const { hasFeatureFlag } = useUserStore();
const { isNativeApp } = useNativeAppStore();

onResult(({ data: { order: orderData } }: any) => {
  order.value = orderData;
  customer.value = orderData.customer;
  email.value = orderData.customer?.email || '';

  if (
    hasFeatureFlag('compliant-register') &&
    !delayPrinting.value &&
    popUpPrint.value &&
    lastReceipt.value &&
    !hasAutoPrinted.value
  ) {
    // Latch eagerly so a re-fired onResult can't double-print while this is in
    // flight; release it if printing fails so a later onResult can retry.
    hasAutoPrinted.value = true;
    printThermalReceipt(lastReceipt.value?.id).catch(() => {
      hasAutoPrinted.value = false;
    });
  }
});

const { companySettings, isLoyaltyPointsEnabled } = useCompanyStore();
const { reset } = useRegisterOrderStore();
const { t } = useI18n();
const router = useRouter();
const { setState } = usePreCreateStore();
const { addOrderToQueue } = useFiscalizationStore();
const { exitCurrentState } = useRegisterStore();

const close = () => {
  exitCurrentState();
  router.replace({ name: 'register' });
};

const showCreateNoteModal = ref(false);
const emailModalDocument = ref<'receipt' | 'invoice' | null>(null);
const showIssueInvoiceModal = ref(false);
const showSendPaymentLinkModal = ref(false);
const promptPrint = ref(false);
const hasAutoPrinted = ref(false);
const popUpPrint = computed(() => companySettings.sales.thermalSupport);
const fiscalizationEnabled = computed(
  () =>
    !!companySettings.fiscalization.tssId ||
    hasFeatureFlag('fiskaly-v2-enabled'),
);

const invoiceDocument = computed(() => {
  const invoices = order.value?.documents?.filter(
    (document) =>
      document.documentType === RegisterDocumentEnum.Invoice ||
      document.documentType === RegisterDocumentEnum.CreditInvoice,
  );
  return invoices?.[invoices.length - 1];
});
const hasInvoice = computed(() => !!invoiceDocument.value);

const autoInvoiceGenerationEnabled = computed(
  () => companySettings.sales.generateInvoiceAutomatically,
);

const autoInvoiceGenerated = computed(
  () =>
    autoInvoiceGenerationEnabled.value &&
    hasFeatureFlag('compliant-register') &&
    hasInvoice.value &&
    props.invoiceJustIssued,
);

const autoInvoiceGenerationFailed = computed(
  () =>
    autoInvoiceGenerationEnabled.value &&
    hasFeatureFlag('compliant-register') &&
    !!order.value?.permissions.issueInvoice &&
    !hasInvoice.value,
);

const issueInvoiceManually = () => {
  segment.track('register.sales.issue_invoice.clicked', {
    origin: 'checkout_modal_alert',
  });
  showIssueInvoiceModal.value = true;
};

const handleIssueInvoiceSubmit = () => {
  refetch();
  showIssueInvoiceModal.value = false;
  router.push({ name: 'sale', params: { id: props.orderId } });
};

const lastReceipt = computed(() => {
  const receipts = order.value?.documents?.filter(
    (document) =>
      document.documentType === RegisterDocumentEnum.Receipt ||
      document.documentType === RegisterDocumentEnum.CorrectionReceipt,
  );
  return receipts?.[receipts.length - 1];
});

const fiscalizationReady = computed(
  () => !fiscalizationEnabled.value || order.value?.fiscalizationFinished,
);
const delayPrinting = computed(
  () => popUpPrint.value && fiscalizationEnabled.value,
);
const printUrl = ref(`${config.backendUrl}/orders/${props.orderId}`);
const pdfUrl = ref(`${printUrl.value}.pdf`);
// background forces the hidden-iframe path when there's no user gesture
// (auto-print after checkout).
const printBackground = ref(false);
const printKey = ref(0);

const startPrint = (url: string, { background = false } = {}) => {
  if (isNativeApp) {
    window.location.href = `${url}?back=1`;
  } else {
    printUrl.value = url;
    printBackground.value = background;
    printKey.value++;
    promptPrint.value = true;
  }
};

const bookNextAppointment = () => {
  if (customer.value) {
    setState({ action: Action.Create, customer: customer.value });
  }
  router.push({ name: 'calendar' });
};

onUnmounted(() => reset());

const listenToOrderFiscalization = () => {
  if (props.orderId) {
    addOrderToQueue(props.orderId);
  }
};

if (delayPrinting.value) {
  listenToOrderFiscalization();
} else if (popUpPrint.value) {
  if (!hasFeatureFlag('compliant-register')) {
    promptPrint.value = true;
  }
}

const onSelectCustomer = (value: Customer) => {
  customer.value = value;
  email.value = value?.email || '';
};

const disableCompliantActions = computed(
  () => hasFeatureFlag('compliant-register') && loadingPdf.value,
);

const hideReceiptActions = computed(
  () =>
    hasFeatureFlag('compliant-register') &&
    (!lastReceipt.value || !!invoiceDocument.value),
);

const hidePaymentLinkAction = computed(
  () =>
    !hasFeatureFlag('compliant-register') ||
    !companySettings.sales.paymentLink ||
    !order.value?.amountOutstanding ||
    (order.value?.amountOutstanding && order.value?.amountOutstanding < 0),
);

// Disables the view/print actions while a print job is in flight
// (disableCompliantActions) or fiscalization data isn't back yet.
const documentActionsDisabled = computed(
  () => !fiscalizationReady.value || disableCompliantActions.value,
);

const actions = computed(() => {
  const actions = [
    {
      name: 'invoice-pdf',
      label: t('global.actions.view_invoice_pdf'),
      icon: 'pdf',
      disabled: documentActionsDisabled.value,
      hide: !hasFeatureFlag('compliant-register') || !invoiceDocument.value,
    },
    {
      name: 'invoice-print',
      label: t('global.actions.print_item', {
        item: t('global.items.invoice', 1),
      }),
      icon: 'printer',
      disabled: documentActionsDisabled.value,
      hide: !hasFeatureFlag('compliant-register') || !invoiceDocument.value,
    },
    {
      name: 'send-invoice',
      label: t('register.send_digital_invoice'),
      icon: 'paper-plane',
      disabled: !fiscalizationReady.value,
      hide: !hasFeatureFlag('compliant-register') || !invoiceDocument.value,
    },
    {
      name: 'pdf',
      label: t('global.actions.view_receipt_pdf'),
      icon: 'pdf',
      disabled: documentActionsDisabled.value,
      hide: hideReceiptActions.value,
    },
    {
      name: 'print',
      label: t('global.actions.print_receipt'),
      icon: 'printer',
      disabled: documentActionsDisabled.value,
      hide: hideReceiptActions.value,
    },
    {
      name: 'email-receipt',
      label: t('register.send_digital_receipt'),
      icon: 'paper-plane',
      hide: hideReceiptActions.value,
    },
    {
      name: 'send-payment-link',
      icon: 'paper-plane',
      label: filters.capitalize(t('global.actions.send_payment_link')),
      hide: hidePaymentLinkAction.value,
    },
    {
      name: 'issue-invoice',
      icon: 'file',
      label: filters.capitalize(t('global.actions.issue_invoice')),
      hide: !order.value?.permissions.issueInvoice || hasInvoice.value,
    },
    {
      name: 'note',
      label: order.value?.comments
        ? t('global.actions.edit_note')
        : t('global.actions.add_note'),
      icon: 'message-circle',
      disabled: updatingNote.value,
    },
  ];

  return actions.filter((action) => !action.hide);
});

const onActionSelect = (action: string) => {
  switch (action) {
    case 'pdf':
      handlePdfAction();
      break;
    case 'print':
      handlePrintAction();
      break;
    case 'note':
      showCreateNoteModal.value = true;
      break;
    case 'email-receipt':
      emailModalDocument.value = 'receipt';
      break;
    case 'invoice-pdf':
      viewDocumentPdf(invoiceDocument.value?.id);
      break;
    case 'invoice-print':
      printDocument(invoiceDocument.value?.id);
      break;
    case 'send-invoice':
      emailModalDocument.value = 'invoice';
      break;
    case 'issue-invoice':
      segment.track('register.sales.issue_invoice.clicked', {
        origin: 'checkout_modal',
      });
      showIssueInvoiceModal.value = true;
      break;
    case 'send-payment-link':
      showSendPaymentLinkModal.value = true;
      break;
  }
};

const viewDocumentPdf = (documentId?: string) => {
  if (!documentId) return;
  const url = `${config.backendUrl}/register/documents/${documentId}/view`;
  isNativeApp ? (window.location.href = url) : window.open(url, '_blank');
};

const handlePdfAction = () => {
  if (hasFeatureFlag('compliant-register')) {
    viewDocumentPdf(lastReceipt.value?.id);
  } else {
    window.open(pdfUrl.value, '_blank');
  }
};

const handlePrintAction = () => {
  if (hasFeatureFlag('compliant-register')) {
    compliantPrintAction();
  } else {
    if (isNativeApp) {
      window.location.href = `${config.backendUrl}/orders/${props.orderId}?back=1`;
    } else {
      promptPrint.value = true;
    }
  }
};

const printDocument = async (documentId?: string) => {
  if (!documentId) return;
  loadingPdf.value = true;
  try {
    startPrint(`/register/documents/${documentId}/print`);
  } finally {
    loadingPdf.value = false;
  }
};

const compliantPrintAction = () => printDocument(lastReceipt.value?.id);

// Auto-print after checkout runs without a user gesture, so window.open would be
// popup-blocked. background=true forces PrintUrl's hidden-iframe path.
const printThermalReceipt = async (documentId?: string) => {
  if (!documentId) return;
  await startPrint(`/register/documents/${documentId}/print`, {
    background: true,
  });
};

const { mutate: updateOrder, loading: updatingNote } =
  useMutation(UPDATE_ORDER);

const updateNote = (note: string) => {
  updateOrder({
    input: {
      id: props.orderId,
      note,
    },
  }).then(({ data: { updateOrder } }: any) => {
    if (!updateOrder.errors && order.value) {
      order.value = { ...order.value, comments: note };
      showCreateNoteModal.value = false;
      flash(t('global.flash.note_updated'));
    }
  });
};
</script>

<style
  lang="scss"
  module
>
.aside {
  padding: $spacing;
  padding-bottom: 0;
}
</style>
